/* eslint-disable import/no-named-as-default */
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  effectiveLanguage,
  effectiveParticipation,
  participationFlags,
  type ActivityLanguageValue,
  type ContentLang,
  type ParticipationValue,
} from '@yoyoclub/shared';
import imageCompression from 'browser-image-compression';
import { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';

import 'react-datepicker/dist/react-datepicker.css';

import { BilingualField } from '../../components/BilingualField';
import { DescriptionEditor } from '../../components/DescriptionEditor';
import { TranslateAllButton, TranslateAllProvider } from '../../components/TranslateAll';
import { RepeatField } from '../../components/admin/RepeatField';
import {
  bilingualPayload,
  emptyBilingual,
  hasBilingualValue,
  toBilingualValue,
  type BilingualValue,
} from '../../components/bilingualValue';
import { HostTourGuideButton, TOUR, tourAttr, useHostTourStore } from '../../features/hostTour';
import { useAuth } from '../../hooks/useAuth';
import { expandSeries, type RepeatConfig } from '../../utils/repeatedSessions';
import { describeSeries, groupSessionsBySeriesId, type Grouped } from '../../utils/seriesGrouping';

interface ActivitySession {
  id?: string;
  startTime: string;
  endTime: string;
  maxCapacity: number;
  price?: string; // Dollar string, empty = use activity default
  adultPrice?: string; // Dollar string, empty = use activity default
  descriptionCN?: string; // Chinese session description
  descriptionEN?: string; // English session description
  descriptionAutoLang?: ContentLang | null; // Side that is an unreviewed machine translation
  isCancelled?: boolean;
  hasBookingHistory?: boolean;
  seriesId?: string | null; // UUID v4 for repeated-series grouping; null/undefined = ad-hoc single
  /** Draft-only: repeat configuration for newly-added sessions. Stripped at submit time after expansion. */
  _repeat?: RepeatConfig;
}

interface ActivityFormData {
  title: BilingualValue;
  description: BilingualValue;
  category: string;
  ageMin: string;
  ageMax: string;
  price: string; // child price in dollars, string to allow empty (field name kept as "price" for backward compat)
  adultPrice: string; // adult price in dollars, string to allow empty (0 = free)
  childRequired: boolean;
  adultRequired: boolean;
  // Single source of childRequired/adultRequired (docs/prd-homepage-redesign.md §4.2)
  participation: ParticipationValue;
  language: ActivityLanguageValue;
  childCountsTowardCapacity: boolean;
  adultCountsTowardCapacity: boolean;
  locationDetails: string;
  region: string[]; // Region enum values; at least 1 required at submit time
  status: string;
  hostId: string;
  sessions: ActivitySession[];
  bookingCutoffOption: string; // 'END' | 'START' | 'HOURS' | 'DAYS'
  bookingCutoffValue: string; // numeric input, only meaningful for HOURS/DAYS
}

interface HostOption {
  id: string;
  displayName: string;
}

interface ImageItem {
  id: string;
  url: string;
  isCover: boolean;
  file?: File;
}

let imageIdCounter = 0;
function generateImageId() {
  return `img-${Date.now()}-${++imageIdCounter}`;
}

function SortableImageItem({
  image,
  index,
  onSetCover,
  onRemove,
}: {
  image: ImageItem;
  index: number;
  onSetCover: (index: number) => void;
  onRemove: (index: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: image.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group" {...attributes} {...listeners}>
      <img
        src={image.url}
        alt={`Activity image ${index + 1}`}
        className={`w-full h-32 object-cover rounded-lg select-none ${
          image.isCover ? 'ring-2 ring-primary' : ''
        } ${isDragging ? 'shadow-lg ring-2 ring-primary' : ''}`}
        draggable={false}
      />
      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onSetCover(index)}
          className="px-2 py-1 bg-white text-xs rounded hover:bg-gray-100"
        >
          {image.isCover ? 'Cover' : 'Set Cover'}
        </button>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onRemove(index)}
          className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
        >
          Remove
        </button>
      </div>
      {image.isCover && (
        <span className="absolute top-2 left-2 px-2 py-1 bg-primary text-white text-xs rounded">
          Cover
        </span>
      )}
      <span className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black bg-opacity-40 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
        Drag
      </span>
    </div>
  );
}

// Alphabetical, with Other kept last.
const CATEGORIES = [
  { value: 'ART', label: 'Art & Craft' },
  { value: 'COOKING', label: 'Cooking' },
  { value: 'DANCE', label: 'Dance' },
  { value: 'MUSIC', label: 'Music' },
  { value: 'NATURE', label: 'Nature & Outdoor' },
  { value: 'SPORTS', label: 'Sports' },
  { value: 'STEM', label: 'STEM' },
  { value: 'OTHER', label: 'Other' },
];

const REGIONS = [
  { value: 'SOUTH_BAY', label: 'South Bay' },
  { value: 'PENINSULA', label: 'Peninsula' },
  { value: 'EAST_BAY', label: 'East Bay' },
  { value: 'SAN_FRANCISCO', label: 'San Francisco' },
  { value: 'OTHER', label: 'Other' },
];

// Registration deadline modes. HOURS/DAYS show a numeric input; the value is
// converted to minutes before each session's start time (backend accepts any
// whole minutes from 0 to 90 days).
const BOOKING_CUTOFF_OPTIONS = [
  { value: 'END', label: 'At activity end time (default)' },
  { value: 'START', label: 'At activity start time' },
  { value: 'HOURS', label: 'Hours before start' },
  { value: 'DAYS', label: 'Days before start' },
];

export default function AdminActivityForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();
  const isEditing = !!id;

  // Detect host vs admin context from the current route
  const isHostContext = location.pathname.startsWith('/host/');
  const apiPrefix = isHostContext ? 'host' : 'admin';
  const listPath = isHostContext ? '/host/activities' : '/admin/activities';

  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [formData, setFormData] = useState<ActivityFormData>({
    title: emptyBilingual(),
    description: emptyBilingual(),
    category: 'OTHER',
    ageMin: '3',
    ageMax: '12',
    price: '',
    adultPrice: '',
    childRequired: true,
    adultRequired: true,
    participation: 'PARENT_CHILD',
    language: 'ZH',
    childCountsTowardCapacity: true,
    adultCountsTowardCapacity: false,
    locationDetails: '',
    region: [],
    status: isHostContext ? 'DRAFT' : 'APPROVED',
    hostId: '',
    sessions: [],
    bookingCutoffOption: 'END',
    bookingCutoffValue: '',
  });

  const [hosts, setHosts] = useState<HostOption[]>([]);
  const [images, setImages] = useState<ImageItem[]>([]);

  // Region multi-select dropdown: open state + outside-click handler
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const regionDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!regionDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(e.target as Node)) {
        setRegionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [regionDropdownOpen]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setImages((prev) => {
        const oldIndex = prev.findIndex((img) => img.id === active.id);
        const newIndex = prev.findIndex((img) => img.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch approved hosts for the host selector (admin context only)
  useEffect(() => {
    if (isHostContext || !isAuthenticated) return;

    const fetchHosts = async () => {
      try {
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const response = await fetch(`${apiUrl}/admin/hosts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            const approvedHosts = result.data
              .filter((h: { status: string }) => h.status === 'APPROVED')
              .map((h: { id: string; displayName: string }) => ({
                id: h.id,
                displayName: h.displayName,
              }));
            setHosts(approvedHosts);

            // For new activity, default to YOYO Club host
            if (!isEditing) {
              const yoyoClubHost = approvedHosts.find(
                (h: HostOption) => h.displayName === 'YOYO Club'
              );
              if (yoyoClubHost) {
                setFormData((prev) => ({ ...prev, hostId: yoyoClubHost.id }));
              }
            }
          }
        }
      } catch {
        // Non-critical: host selector will just be empty
      }
    };

    fetchHosts();
  }, [isHostContext, isAuthenticated, isEditing, getToken]);

  // Fetch activity data if editing
  useEffect(() => {
    if (!isEditing || !isAuthenticated) return;

    const fetchActivity = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

        const response = await fetch(`${apiUrl}/${apiPrefix}/activities/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch activity');
        }

        const result = await response.json();
        if (result.success && result.data) {
          const activity = result.data;

          const formattedSessions = (activity.sessions || []).map(
            (session: {
              id: string;
              startTime: string;
              endTime: string;
              maxCapacity: number;
              price?: number | null;
              adultPrice?: number | null;
              descriptionCN?: string | null;
              descriptionEN?: string | null;
              descriptionAutoLang?: ContentLang | null;
              isCancelled?: boolean;
              hasBookingHistory?: boolean;
              seriesId?: string | null;
            }) => ({
              id: session.id,
              startTime: session.startTime,
              endTime: session.endTime,
              maxCapacity: session.maxCapacity,
              price: session.price != null ? String(session.price / 100) : '',
              adultPrice: session.adultPrice != null ? String(session.adultPrice / 100) : '',
              descriptionCN: session.descriptionCN || '',
              descriptionEN: session.descriptionEN || '',
              descriptionAutoLang: session.descriptionAutoLang ?? null,
              isCancelled: session.isCancelled || false,
              hasBookingHistory: session.hasBookingHistory || false,
              seriesId: session.seriesId ?? null,
            })
          );

          setFormData({
            title: toBilingualValue({
              zh: activity.titleCN,
              en: activity.titleEN,
              autoLang: activity.titleAutoLang,
            }),
            description: toBilingualValue({
              zh: activity.descriptionCN,
              en: activity.descriptionEN,
              autoLang: activity.descriptionAutoLang,
            }),
            category: activity.category || 'OTHER',
            ageMin: String(activity.ageMin ?? 3),
            ageMax: String(activity.ageMax ?? 12),
            price: activity.price ? String(activity.price / 100) : '', // Convert cents to dollars for display
            adultPrice: activity.adultPrice ? String(activity.adultPrice / 100) : '',
            childRequired: activity.childRequired ?? true,
            adultRequired: activity.adultRequired ?? true,
            participation: effectiveParticipation(activity),
            language: effectiveLanguage(activity),
            childCountsTowardCapacity: activity.childCountsTowardCapacity ?? true,
            adultCountsTowardCapacity: activity.adultCountsTowardCapacity ?? false,
            locationDetails: activity.locationDetails || '',
            region: Array.isArray(activity.region) ? activity.region : [],
            status: activity.status || 'APPROVED',
            hostId: activity.hostId || '',
            sessions: formattedSessions,
            // Map stored minutes back to the friendliest mode: 0 → START,
            // clean multiples of a day → DAYS, otherwise HOURS. The API only
            // accepts whole hours, so the division is always exact and a
            // saved value round-trips unchanged.
            ...(activity.bookingCutoffMinutes == null
              ? { bookingCutoffOption: 'END', bookingCutoffValue: '' }
              : activity.bookingCutoffMinutes === 0
                ? { bookingCutoffOption: 'START', bookingCutoffValue: '' }
                : activity.bookingCutoffMinutes % 1440 === 0
                  ? {
                      bookingCutoffOption: 'DAYS',
                      bookingCutoffValue: String(activity.bookingCutoffMinutes / 1440),
                    }
                  : {
                      bookingCutoffOption: 'HOURS',
                      bookingCutoffValue: String(activity.bookingCutoffMinutes / 60),
                    }),
          });
          setImages(
            activity.images?.map((img: { imageUrl: string; isCover: boolean }) => ({
              id: generateImageId(),
              url: img.imageUrl,
              isCover: img.isCover,
            })) || []
          );
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load activity';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [isEditing, id, isAuthenticated, getToken, apiPrefix]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    // Keep these numeric inputs as strings so clearing the field shows the
    // placeholder instead of a forced 0.
    const isStringField = name === 'ageMin' || name === 'ageMax' || name === 'bookingCutoffValue';
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' && !isStringField ? Number(value) : value,
    }));
  };

  const handleAddSession = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);

    const endTime = new Date(tomorrow);
    endTime.setHours(12, 0, 0, 0);

    setFormData((prev) => ({
      ...prev,
      sessions: [
        ...prev.sessions,
        {
          startTime: tomorrow.toISOString(),
          endTime: endTime.toISOString(),
          maxCapacity: 10,
          price: '',
          adultPrice: '',
          descriptionCN: '',
          descriptionEN: '',
          descriptionAutoLang: null,
          _repeat: { mode: 'NEVER' },
        },
      ],
    }));
  };

  const handleSessionRepeatChange = (index: number, repeat: RepeatConfig) => {
    setFormData((prev) => ({
      ...prev,
      sessions: prev.sessions.map((session, i) =>
        i === index ? { ...session, _repeat: repeat } : session
      ),
    }));
  };

  const handleSessionDescriptionChange = (index: number, v: BilingualValue) => {
    setFormData((prev) => ({
      ...prev,
      sessions: prev.sessions.map((session, i) =>
        i === index
          ? {
              ...session,
              descriptionCN: v.zh,
              descriptionEN: v.en,
              descriptionAutoLang: v.autoLang,
            }
          : session
      ),
    }));
  };

  const handleSessionChange = (
    index: number,
    field: keyof ActivitySession,
    value: string | number
  ) => {
    setFormData((prev) => ({
      ...prev,
      sessions: prev.sessions.map((session, i) => {
        if (i !== index) return session;
        const updated = { ...session, [field]: value };
        // When startTime changes, auto-adjust endTime if it's empty or before the new startTime
        if (field === 'startTime' && typeof value === 'string') {
          const newStart = new Date(value);
          const currentEnd = updated.endTime ? new Date(updated.endTime) : null;
          if (!currentEnd || currentEnd <= newStart) {
            const defaultEnd = new Date(newStart.getTime() + 30 * 60 * 1000);
            updated.endTime = defaultEnd.toISOString();
          }
        }
        return updated;
      }),
    }));
  };

  const handleRemoveSession = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      sessions: prev.sessions.filter((_, i) => i !== index),
    }));
  };

  const handleCancelSession = async (index: number) => {
    const session = formData.sessions[index];
    if (!session.id) return;

    if (
      !confirm(
        'Are you sure you want to cancel this session? All active bookings will be marked as cancelled and affected users will be notified by email automatically.'
      )
    ) {
      return;
    }

    try {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/sessions/${session.id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel session');
      }

      setFormData((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s, i) => (i === index ? { ...s, isCancelled: true } : s)),
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel session');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    setError(null);

    try {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

      for (const originalFile of Array.from(files)) {
        // Compress image if it's an image file
        let file = originalFile;
        if (originalFile.type.startsWith('image/')) {
          file = await imageCompression(originalFile, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
        }

        // Get presigned URL
        const presignedResponse = await fetch(`${apiUrl}/upload/presigned-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            fileName: file.name,
            fileType: file.type,
            folder: 'activities',
          }),
        });

        if (!presignedResponse.ok) {
          throw new Error('Failed to get upload URL');
        }

        const presignedResult = await presignedResponse.json();
        const { uploadUrl, fileUrl } = presignedResult.data;

        // Upload to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file');
        }

        // Add to images list
        setImages((prev) => [
          ...prev,
          { id: generateImageId(), url: fileUrl, isCover: prev.length === 0, file },
        ]);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload images';
      setError(errorMessage);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleSetCover = (index: number) => {
    setImages((prev) =>
      prev.map((img, i) => ({
        ...img,
        isCover: i === index,
      }))
    );
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => {
      const newImages = prev.filter((_, i) => i !== index);
      // If removed image was cover, set first image as cover
      if (prev[index].isCover && newImages.length > 0) {
        newImages[0].isCover = true;
      }
      return newImages;
    });
  };

  /** Uploads a picture for the description text and returns its public URL. */
  const uploadDescriptionImage = async (originalFile: File): Promise<string> => {
    setError(null);
    const token = await getToken();
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
    const file = originalFile.type.startsWith('image/')
      ? await imageCompression(originalFile, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        })
      : originalFile;

    const presignedResponse = await fetch(`${apiUrl}/upload/presigned-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        fileName: file.name,
        fileType: file.type,
        folder: 'activities',
      }),
    });
    if (!presignedResponse.ok) {
      throw new Error('Failed to get upload URL');
    }
    const presignedResult = await presignedResponse.json();
    const { uploadUrl, fileUrl } = presignedResult.data;

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file');
    }
    return fileUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasBilingualValue(formData.title)) {
      setError('Enter a title in Chinese or English');
      return;
    }

    if (formData.region.length === 0) {
      setError('Select at least one region');
      return;
    }

    const ageMinNum = Number(formData.ageMin);
    const ageMaxNum = Number(formData.ageMax);
    if (formData.childRequired && (isNaN(ageMinNum) || ageMinNum < 0 || ageMinNum % 0.5 !== 0)) {
      setError('Min age must be a non-negative multiple of 0.5 (e.g. 1, 1.5, 2)');
      return;
    }
    if (formData.childRequired && (isNaN(ageMaxNum) || ageMaxNum < 0 || ageMaxNum % 0.5 !== 0)) {
      setError('Max age must be a non-negative multiple of 0.5 (e.g. 1, 1.5, 2)');
      return;
    }
    if (formData.childRequired && ageMinNum > ageMaxNum) {
      setError('Min age cannot be greater than max age');
      return;
    }

    if (formData.sessions.length === 0) {
      setError('At least one session is required');
      return;
    }

    // Validate session end times are after start times
    for (const session of formData.sessions) {
      if (session.isCancelled) continue;
      if (
        session.startTime &&
        session.endTime &&
        new Date(session.endTime) <= new Date(session.startTime)
      ) {
        setError('Session end time must be after start time');
        return;
      }
    }

    // Expand any new session whose Repeat is non-Never into a fresh series with a shared
    // UUID v4 seriesId. Each occurrence inherits the anchor's pricing / capacity / description
    // (PRD §4.5). Existing sessions (with id) and Never-mode new sessions pass through.
    const expandedSessions: ActivitySession[] = [];
    for (const session of formData.sessions) {
      const repeat = session._repeat;
      if (session.id || !repeat || repeat.mode === 'NEVER') {
        expandedSessions.push(session);
        continue;
      }
      const result = expandSeries(
        { startTime: session.startTime, endTime: session.endTime },
        repeat
      );
      if (!result.ok) {
        setError(result.error);
        return;
      }
      const seriesId = crypto.randomUUID();
      for (const occ of result.occurrences) {
        expandedSessions.push({
          ...session,
          startTime: occ.startTime,
          endTime: occ.endTime,
          seriesId,
        });
      }
    }

    // Registration deadline: HOURS/DAYS need a positive whole number
    // (converted to minutes; backend caps at 90 days).
    let bookingCutoffMinutes: number | null = null;
    if (formData.bookingCutoffOption === 'START') {
      bookingCutoffMinutes = 0;
    } else if (
      formData.bookingCutoffOption === 'HOURS' ||
      formData.bookingCutoffOption === 'DAYS'
    ) {
      const isDays = formData.bookingCutoffOption === 'DAYS';
      const value = Number(formData.bookingCutoffValue);
      const maxValue = isDays ? 90 : 90 * 24;
      if (!Number.isInteger(value) || value < 1 || value > maxValue) {
        setError(
          isDays
            ? 'Days before start must be a whole number between 1 and 90'
            : `Hours before start must be a whole number between 1 and ${90 * 24}`
        );
        return;
      }
      bookingCutoffMinutes = value * (isDays ? 1440 : 60);
    }

    try {
      setSubmitting(true);
      setError(null);

      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

      const {
        bookingCutoffOption: _cutoffOption,
        bookingCutoffValue: _cutoffValue,
        title,
        description,
        ...formDataForPayload
      } = formData;
      const payload = {
        ...formDataForPayload,
        ...bilingualPayload(title, 'title'),
        ...bilingualPayload(description, 'description'),
        bookingCutoffMinutes,
        bookingCutoffAt: null,
        ageMin: formData.ageMin === '' ? 3 : ageMinNum,
        ageMax: formData.ageMax === '' ? 12 : ageMaxNum,
        price: formData.price === '' ? 0 : Math.round(Number(formData.price) * 100), // Convert to cents
        adultPrice: formData.adultPrice === '' ? 0 : Math.round(Number(formData.adultPrice) * 100),
        hostId: !isHostContext && formData.hostId ? formData.hostId : undefined,
        sessions: expandedSessions.map((session) => {
          // Strip the draft-only _repeat marker; it must not be sent to the backend.
          const { _repeat: _unused, ...rest } = session;
          return {
            ...rest,
            price:
              session.price === '' || session.price === undefined
                ? null
                : Math.round(Number(session.price) * 100),
            adultPrice:
              session.adultPrice === '' || session.adultPrice === undefined
                ? null
                : Math.round(Number(session.adultPrice) * 100),
            descriptionCN: session.descriptionCN?.trim() || null,
            descriptionEN: session.descriptionEN?.trim() || null,
            descriptionAutoLang: session.descriptionAutoLang ?? null,
          };
        }),
        images: images.map((img) => ({
          imageUrl: img.url,
          isCover: img.isCover,
        })),
      };

      const url = isEditing
        ? `${apiUrl}/${apiPrefix}/activities/${id}`
        : `${apiUrl}/${apiPrefix}/activities`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save activity');
      }

      const result = await response.json();

      // Host creating new activity that needs admin approval
      if (isHostContext && !isEditing && result.data?.status === 'DRAFT') {
        alert('Activity submitted for review. An admin will review and publish it shortly.');
      }

      // Let the walkthrough follow up on the list page after a real submit.
      // The list files an approved activity under Active only if a session is
      // still upcoming; otherwise it lands under Past.
      if (isHostContext && !isEditing) {
        const now = Date.now();
        const hasUpcoming = expandedSessions.some((s) => new Date(s.endTime).getTime() >= now);
        const outcome =
          result.data?.status !== 'APPROVED' ? 'pending' : hasUpcoming ? 'live' : 'past';
        useHostTourStore.getState().markFormSubmitted('activity-form', outcome);
      }

      navigate(listPath);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save activity';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <TranslateAllProvider>
      <div className="min-h-screen bg-gray-100">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Link to={listPath} className="text-gray-500 hover:text-gray-700">
                Back
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Edit Activity' : 'Create Activity'}
              </h1>
              {isHostContext && (
                <div className="ml-auto">
                  <HostTourGuideButton />
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Form */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
              {error}
            </div>
          )}

          <TranslateAllButton className="mb-4 justify-end" />

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Images */}
            <div className="bg-white rounded-lg shadow-sm p-6" {...tourAttr(TOUR.afMedia)}>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Cover Image</h2>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <SortableContext
                    items={images.map((img) => img.id)}
                    strategy={rectSortingStrategy}
                  >
                    {images.map((image, index) => (
                      <SortableImageItem
                        key={image.id}
                        image={image}
                        index={index}
                        onSetCover={handleSetCover}
                        onRemove={handleRemoveImage}
                      />
                    ))}
                  </SortableContext>

                  {/* Upload Button */}
                  <label className="h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-gray-50 transition-colors">
                    <input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImages}
                    />
                    {uploadingImages ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    ) : (
                      <>
                        <span className="text-2xl text-gray-400 mb-1">+</span>
                        <span className="text-sm text-gray-500">Upload</span>
                      </>
                    )}
                  </label>
                </div>
              </DndContext>

              <p className="text-sm text-gray-500">
                Supported formats: JPG, PNG, WEBP, MP4, MOV. Drag to reorder. Click "Set Cover" to
                choose cover image.
              </p>
            </div>

            {/* Basic Info */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <BilingualField
                  className="md:col-span-2"
                  fieldType="activityTitle"
                  label="Title"
                  required
                  value={formData.title}
                  onChange={(title) => setFormData((prev) => ({ ...prev, title }))}
                  {...tourAttr(TOUR.afTitle)}
                />

                <DescriptionEditor
                  className="md:col-span-2"
                  fieldType="activityDescription"
                  label="Description"
                  rows={6}
                  value={formData.description}
                  onChange={(description) => setFormData((prev) => ({ ...prev, description }))}
                  uploadImage={uploadDescriptionImage}
                  onUploadError={setError}
                  {...tourAttr(TOUR.afDescription)}
                />

                <div {...tourAttr(TOUR.afCategory)}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Activity Type: sets which prices apply and the age range */}
                <div {...tourAttr(TOUR.afType)}>
                  <label
                    htmlFor="activity-type"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Who Takes Part
                  </label>
                  <select
                    id="activity-type"
                    value={formData.participation}
                    onChange={(e) => {
                      const participation = e.target.value as ParticipationValue;
                      setFormData((prev) =>
                        participation === 'ADULT_ONLY'
                          ? {
                              ...prev,
                              participation,
                              ...participationFlags(participation),
                              price: '', // child price not applicable for adult-only
                              ageMin: '18',
                              ageMax: '99',
                              adultCountsTowardCapacity: true,
                            }
                          : {
                              ...prev,
                              participation,
                              ...participationFlags(participation),
                              // Leaving adult-only resets the 18-99 range to a children's default
                              ...(prev.childRequired ? {} : { ageMin: '3', ageMax: '12' }),
                            }
                      );
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="PARENT_CHILD">
                      Parent and child (a child attends with an adult)
                    </option>
                    <option value="KID_ONLY">Kids only (adults do not need to sign up)</option>
                    <option value="ADULT_ONLY">Adults only</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="activity-language"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Teaching Language <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="activity-language"
                    value={formData.language}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        language: e.target.value as ActivityLanguageValue,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="ZH">Chinese</option>
                    <option value="EN">English</option>
                    <option value="BILINGUAL">Bilingual</option>
                  </select>
                </div>

                {/* price field = child price (field name kept as "price" for backward compat) */}
                <div {...tourAttr(TOUR.afChildPrice)}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Child Price (USD){' '}
                    {formData.childRequired && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={(e) => setFormData((prev) => ({ ...prev, price: e.target.value }))}
                    min="0"
                    step="0.01"
                    required={formData.childRequired}
                    disabled={!formData.childRequired}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  {!formData.childRequired && (
                    <p className="mt-1 text-xs text-gray-500">
                      Not applicable for adult-only activities.
                    </p>
                  )}
                </div>

                <div {...tourAttr(TOUR.afAdultPrice)}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adult Price (USD)
                  </label>
                  <input
                    type="number"
                    name="adultPrice"
                    value={formData.adultPrice}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, adultPrice: e.target.value }))
                    }
                    min="0"
                    step="0.01"
                    placeholder="Leave empty = free"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Leave empty or enter 0 for free adult admission.
                  </p>
                </div>

                {/* Capacity Strategy */}
                {formData.childRequired && (
                  <div className="md:col-span-2" {...tourAttr(TOUR.afCapacity)}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Capacity Counting
                    </label>
                    <div className="space-y-2">
                      {(!formData.price || Number(formData.price) === 0) && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.childCountsTowardCapacity}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                childCountsTowardCapacity: e.target.checked,
                              }))
                            }
                            className="rounded text-primary focus:ring-primary"
                          />
                          <span className="text-sm">Free children count toward capacity</span>
                        </label>
                      )}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.adultCountsTowardCapacity}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              adultCountsTowardCapacity: e.target.checked,
                            }))
                          }
                          className="rounded text-primary focus:ring-primary"
                        />
                        <span className="text-sm">Adults count toward capacity</span>
                      </label>
                    </div>
                  </div>
                )}

                <div {...tourAttr(TOUR.afAge)}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Age</label>
                  <input
                    type="number"
                    name="ageMin"
                    value={formData.ageMin}
                    onChange={handleChange}
                    step="0.5"
                    min={formData.childRequired ? 0 : 18}
                    max={formData.childRequired ? 17 : 99}
                    disabled={!formData.childRequired}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                  />
                  {!formData.childRequired && (
                    <p className="mt-1 text-xs text-gray-500">Fixed to 18+ for adult-only.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Age</label>
                  <input
                    type="number"
                    name="ageMax"
                    value={formData.ageMax}
                    onChange={handleChange}
                    step="0.5"
                    min={formData.childRequired ? 0 : 18}
                    max={formData.childRequired ? 18 : 99}
                    disabled={!formData.childRequired}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </div>

                <div ref={regionDropdownRef} className="relative" {...tourAttr(TOUR.afRegions)}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Regions <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setRegionDropdownOpen((v) => !v)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <span className={formData.region.length === 0 ? 'text-gray-400' : ''}>
                      {formData.region.length === 0
                        ? '— Select region(s) —'
                        : formData.region
                            .map((v) => REGIONS.find((r) => r.value === v)?.label ?? v)
                            .join(', ')}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-500 transition-transform ${regionDropdownOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {regionDropdownOpen && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-1 max-h-64 overflow-y-auto">
                      {REGIONS.map((r) => {
                        const checked = formData.region.includes(r.value);
                        return (
                          <label
                            key={r.value}
                            className="flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setFormData((prev) => ({
                                  ...prev,
                                  region: e.target.checked
                                    ? [...prev.region, r.value]
                                    : prev.region.filter((v) => v !== r.value),
                                }));
                              }}
                              className="w-4 h-4 text-primary focus:ring-primary"
                            />
                            <span className="text-sm">{r.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div {...tourAttr(TOUR.afLocation)}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    name="locationDetails"
                    value={formData.locationDetails}
                    onChange={handleChange}
                    placeholder=""
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Status selector: only visible to admins */}
                {!isHostContext && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="APPROVED">Active (Published)</option>
                      <option value="DRAFT">Draft</option>
                    </select>
                  </div>
                )}

                {/* Host selector: only visible to admins */}
                {!isHostContext && hosts.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
                    <select
                      name="hostId"
                      value={formData.hostId}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {hosts.map((host) => (
                        <option key={host.id} value={host.id}>
                          {host.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Sessions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Sessions</h2>
                <button
                  type="button"
                  onClick={handleAddSession}
                  {...tourAttr(TOUR.afAddSession)}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  + Add Session
                </button>
              </div>

              {/* Registration deadline (applies to every session of this activity) */}
              <div className="mb-6 pb-6" {...tourAttr(TOUR.afDeadline)}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registration deadline
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  When registration closes for each session. Applies to all sessions of this
                  activity.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    name="bookingCutoffOption"
                    value={formData.bookingCutoffOption}
                    onChange={handleChange}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    {BOOKING_CUTOFF_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {(formData.bookingCutoffOption === 'HOURS' ||
                    formData.bookingCutoffOption === 'DAYS') && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        name="bookingCutoffValue"
                        value={formData.bookingCutoffValue}
                        onChange={handleChange}
                        min={1}
                        max={formData.bookingCutoffOption === 'DAYS' ? 90 : 90 * 24}
                        step={1}
                        placeholder={formData.bookingCutoffOption === 'DAYS' ? 'e.g. 3' : 'e.g. 48'}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                      <span className="text-sm text-gray-600">
                        {formData.bookingCutoffOption === 'DAYS'
                          ? 'days before start'
                          : 'hours before start'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {formData.sessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No sessions added. Click "Add Session" to create one.
                </div>
              ) : (
                <SessionFormList
                  sessions={formData.sessions}
                  formData={formData}
                  onSessionChange={handleSessionChange}
                  onSessionDescriptionChange={handleSessionDescriptionChange}
                  onRepeatChange={handleSessionRepeatChange}
                  onRemoveSession={handleRemoveSession}
                  onCancelSession={handleCancelSession}
                  isHostContext={isHostContext}
                />
              )}
            </div>

            {/* Submit */}
            <div className="flex items-center justify-end gap-4">
              <Link
                to={listPath}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                {...tourAttr(TOUR.afSubmit)}
              >
                {submitting ? 'Saving...' : isEditing ? 'Update Activity' : 'Create Activity'}
              </button>
            </div>
          </form>
        </main>
      </div>
    </TranslateAllProvider>
  );
}

function SessionFormItem({
  session,
  index,
  formData,
  onSessionChange,
  onSessionDescriptionChange,
  onRepeatChange,
  onRemoveSession,
  onCancelSession,
  isPast,
  isHostContext,
}: {
  session: ActivitySession;
  index: number;
  formData: ActivityFormData;
  onSessionChange: (index: number, field: keyof ActivitySession, value: string | number) => void;
  onSessionDescriptionChange: (index: number, v: BilingualValue) => void;
  onRepeatChange: (index: number, repeat: RepeatConfig) => void;
  onRemoveSession: (index: number) => void;
  onCancelSession: (index: number) => void;
  isPast: boolean;
  isHostContext: boolean;
}) {
  const isDisabled = isPast || session.isCancelled;
  // Admin can still edit time fields on past sessions (e.g. to fix data entry errors)
  const isTimeDisabled = session.isCancelled || (isPast && isHostContext);
  return (
    <div
      className={`p-4 border rounded-lg space-y-3 ${session.isCancelled ? 'border-red-200 bg-red-50/30' : isPast ? 'border-gray-100 bg-gray-50/50' : 'border-gray-200'}`}
      {...tourAttr(TOUR.afSessionItem)}
    >
      {/* Row 1: Time, Capacity, Remove/Cancel */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <label
            className={`block text-sm font-medium mb-1 ${isTimeDisabled ? 'text-gray-400' : 'text-gray-700'}`}
          >
            Start Time
          </label>
          <DatePicker
            selected={session.startTime ? new Date(session.startTime) : null}
            onChange={(date: Date | null) => {
              if (date) {
                onSessionChange(index, 'startTime', date.toISOString());
              }
            }}
            disabled={isTimeDisabled}
            showTimeSelect
            showTimeInput
            timeInputLabel="Time:"
            dateFormat="MMM d, yyyy h:mm aa"
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${isTimeDisabled ? 'text-gray-400' : ''}`}
            wrapperClassName="w-full"
          />
        </div>

        <div className="flex-1 min-w-[200px]">
          <label
            className={`block text-sm font-medium mb-1 ${isTimeDisabled ? 'text-gray-400' : 'text-gray-700'}`}
          >
            End Time
          </label>
          <DatePicker
            selected={session.endTime ? new Date(session.endTime) : null}
            onChange={(date: Date | null) => {
              if (date) {
                // Prevent setting endTime before startTime
                const start = session.startTime ? new Date(session.startTime) : null;
                if (start && date <= start) {
                  return;
                }
                onSessionChange(index, 'endTime', date.toISOString());
              }
            }}
            disabled={isTimeDisabled}
            showTimeSelect
            showTimeInput
            timeInputLabel="Time:"
            dateFormat="MMM d, yyyy h:mm aa"
            minDate={session.startTime ? new Date(session.startTime) : undefined}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${isTimeDisabled ? 'text-gray-400' : ''}`}
            wrapperClassName="w-full"
          />
        </div>

        <div className="w-32">
          <label
            className={`block text-sm font-medium mb-1 ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}
          >
            Capacity
          </label>
          <input
            type="number"
            value={session.maxCapacity || ''}
            onChange={(e) => onSessionChange(index, 'maxCapacity', Number(e.target.value))}
            disabled={isDisabled}
            min="1"
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${isDisabled ? 'text-gray-400' : ''}`}
          />
        </div>

        {session.isCancelled ? (
          <span className="px-3 py-2 text-red-500 text-sm font-medium">Cancelled</span>
        ) : isPast ? null : session.hasBookingHistory ? (
          isHostContext ? (
            <button
              type="button"
              onClick={() => alert('请联系管理员取消此活动')}
              className="px-3 py-2 text-gray-400 hover:bg-gray-50 rounded-lg transition-colors text-sm"
            >
              Cancel Session
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onCancelSession(index)}
              className="px-3 py-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors text-sm"
            >
              Cancel Session
            </button>
          )
        ) : (
          <button
            type="button"
            onClick={() => onRemoveSession(index)}
            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            Remove
          </button>
        )}
      </div>

      {/* Row 1b: Repeat (only for new sessions — existing rows can't change repeat config) */}
      {!session.id && !isDisabled && session._repeat && (
        <div {...tourAttr(TOUR.afSessionRepeat)}>
          <RepeatField
            anchorStartISO={session.startTime}
            anchorEndISO={session.endTime}
            value={session._repeat}
            onChange={(repeat) => onRepeatChange(index, repeat)}
          />
        </div>
      )}

      {/* Row 2: Session-specific pricing (optional) */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="w-40">
          <label className="block text-xs font-medium text-gray-500 mb-1">Child Price (USD)</label>
          <input
            type="number"
            value={session.price || ''}
            onChange={(e) => onSessionChange(index, 'price', e.target.value)}
            disabled={isDisabled}
            min="0"
            step="0.01"
            placeholder={`Default: $${formData.price || '0'}`}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${isDisabled ? 'text-gray-400' : ''}`}
          />
        </div>
        <div className="w-40">
          <label className="block text-xs font-medium text-gray-500 mb-1">Adult Price (USD)</label>
          <input
            type="number"
            value={session.adultPrice || ''}
            onChange={(e) => onSessionChange(index, 'adultPrice', e.target.value)}
            disabled={isDisabled}
            min="0"
            step="0.01"
            placeholder={`Default: $${formData.adultPrice || '0'}`}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${isDisabled ? 'text-gray-400' : ''}`}
          />
        </div>
        <p className="text-xs text-gray-400 self-center">
          Leave empty to use activity default price
        </p>
      </div>

      {/* Row 3: Description (optional, zh/en pair) */}
      <BilingualField
        compact
        fieldType="sessionDescription"
        label="Description"
        value={{
          zh: session.descriptionCN || '',
          en: session.descriptionEN || '',
          autoLang: session.descriptionAutoLang ?? null,
        }}
        onChange={(v) => onSessionDescriptionChange(index, v)}
        disabled={isDisabled}
        translateOnMount={!isDisabled}
        placeholder={{ ZH: '如：节日特别版、初级班', EN: 'e.g., Holiday special, Beginner level' }}
        labelClassName="block text-xs font-medium text-gray-500 mb-1"
        inputClassName={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${isDisabled ? 'text-gray-400' : ''}`}
      />
    </div>
  );
}

function SessionFormList({
  sessions,
  formData,
  onSessionChange,
  onSessionDescriptionChange,
  onRepeatChange,
  onRemoveSession,
  onCancelSession,
  isHostContext,
}: {
  sessions: ActivitySession[];
  formData: ActivityFormData;
  onSessionChange: (index: number, field: keyof ActivitySession, value: string | number) => void;
  onSessionDescriptionChange: (index: number, v: BilingualValue) => void;
  onRepeatChange: (index: number, repeat: RepeatConfig) => void;
  onRemoveSession: (index: number) => void;
  onCancelSession: (index: number) => void;
  isHostContext: boolean;
}) {
  const [showPast, setShowPast] = useState(false);
  // Series within Edit start expanded (PRD §5.3). Caller can collapse individually.
  const [collapsedSeries, setCollapsedSeries] = useState<Set<string>>(new Set());

  const now = new Date();
  const upcomingWithIndex: { session: ActivitySession; index: number }[] = [];
  const pastWithIndex: { session: ActivitySession; index: number }[] = [];

  sessions.forEach((session, index) => {
    if (session.endTime && new Date(session.endTime) < now) {
      pastWithIndex.push({ session, index });
    } else {
      upcomingWithIndex.push({ session, index });
    }
  });

  const toggleSeries = (seriesId: string) => {
    setCollapsedSeries((prev) => {
      const next = new Set(prev);
      if (next.has(seriesId)) next.delete(seriesId);
      else next.add(seriesId);
      return next;
    });
  };

  const renderItem = (entry: { session: ActivitySession; index: number }, isPast: boolean) => (
    <SessionFormItem
      key={entry.index}
      session={entry.session}
      index={entry.index}
      formData={formData}
      onSessionChange={onSessionChange}
      onSessionDescriptionChange={onSessionDescriptionChange}
      onRepeatChange={onRepeatChange}
      onRemoveSession={onRemoveSession}
      onCancelSession={onCancelSession}
      isPast={isPast}
      isHostContext={isHostContext}
    />
  );

  const renderGrouped = (groups: Grouped<ActivitySession>[], isPast: boolean) =>
    groups.map((g) => {
      if (g.kind === 'single') return renderItem(g.entry, isPast);
      const collapsed = collapsedSeries.has(g.seriesId);
      return (
        <div key={g.seriesId} className="space-y-3">
          <SeriesBanner
            seriesId={g.seriesId}
            caption={describeSeries(g.summary)}
            collapsed={collapsed}
            onToggle={() => toggleSeries(g.seriesId)}
          />
          {!collapsed && (
            <div className="ml-3 pl-3 border-l-2 border-gray-200 space-y-4">
              {g.entries.map((e) => renderItem(e, isPast))}
            </div>
          )}
        </div>
      );
    });

  return (
    <div className="space-y-4">
      {/* Upcoming sessions */}
      {renderGrouped(groupSessionsBySeriesId(upcomingWithIndex), false)}

      {/* Past sessions - collapsible */}
      {pastWithIndex.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowPast(!showPast)}
            className="w-full flex items-center justify-between py-2 px-3 text-sm text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span>
              {pastWithIndex.length} past session{pastWithIndex.length > 1 ? 's' : ''}
            </span>
            <svg
              className={`w-4 h-4 transition-transform ${showPast ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {showPast && (
            <div className="space-y-4 mt-2">
              {renderGrouped(groupSessionsBySeriesId(pastWithIndex), true)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SeriesBanner({
  caption,
  collapsed,
  onToggle,
}: {
  seriesId: string;
  caption: string;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center gap-2 text-sm text-blue-900">
        <span aria-hidden>🔁</span>
        <span className="font-medium">{caption}</span>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="text-xs text-blue-700 hover:text-blue-900 underline"
        aria-expanded={!collapsed}
      >
        {collapsed ? 'Expand' : 'Collapse'}
      </button>
    </div>
  );
}
